

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { AppStep, Essay, Assessment, ModelResult } from './src/types';
import { User } from 'firebase/auth';
import { APP_TITLE, NUMBER_OF_MARKING_RUNS, DEFAULT_SYSTEM_INSTRUCTIONS, QUOTA_EXCEEDED_USER_MESSAGE, ADMIN_USER_EMAILS, MODELS, DEFAULT_MODEL_ID, MARKING_PROMPT_VERSION } from './constants';
import * as dataService from './services/assessmentService';
import { auth } from './services/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import LoginScreen from './components/LoginScreen';
import AssessmentSelection from './components/AssessmentSelection';
import CreateAssessmentForm from './components/CreateAssessmentForm';
import EssayDetailsForm from './components/EssayDetailsForm';
import ResultsDisplay from './components/ResultsDisplay';
import LoadingSpinner from './components/LoadingSpinner';
import Button from './components/Button';
import StepIndicator from './components/StepIndicator';
import AdminDashboard from './components/AdminDashboard';
import ConfirmModal from './components/ConfirmModal';
import ApiKeyHelpModal from './components/ApiKeyHelpModal';
import DomainAuthorizationHelpModal from './components/DomainAuthorizationHelpModal';
import QuotaHelpModal from './components/QuotaHelpModal';
import StorageRulesHelpModal from './components/StorageRulesHelpModal';
import MarkingModelsSelectionModal from './components/MarkingModelsSelectionModal';
import CreditConfirmationModal from './components/CreditConfirmationModal';
import CreditPurchaseModal from './components/CreditPurchaseModal';
import BurgerMenu from './components/BurgerMenu';
import AccountPage from './components/AccountPage';
import AboutPage from './components/AboutPage';
import HomePage from './components/HomePage';
import HelpContactPage from './components/HelpContactPage';
import ContactUsPage from './components/ContactUsPage';
import Footer from './components/Footer';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import RefundPolicyPage from './components/RefundPolicyPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import BatchUploadPanel from './components/BatchUploadPanel';
import { transcribeImage, markEssay, generateFeedback, generateConsolidatedJustifications, runMetaAnalysis, explainMarkingVariance } from './services/aiService';
import { stripTeacherComments, extractTextFromFile, getServerTimestamp, calculateStdDev, calculateWordCount, sanitizeModelResult } from './services/assessmentService';
import { 
  initializeCreditCosts, 
  consumeCredits, 
  refundCredits, 
  getCreditBalance,
  hasSufficientCredits,
  estimateCreditsForJob
} from './services/creditService';
import { initializeCreditPacks } from './services/stripeService';
import { estimateEssayMarkingCredits } from './services/creditEstimationService';
import CreditBalance from './components/CreditBalance';
import { usePreferencesStore } from './src/store/preferencesStore';
import { DashboardLayout, ContentContainer } from './components/layout';

const STEP_TO_HASH: Record<AppStep, string> = {
  [AppStep.HOME]: '#/',
  [AppStep.AUTH]: '#/auth',
  [AppStep.CHOOSE_ASSESSMENT]: '#/assessments',
  [AppStep.CREATE_ASSESSMENT]: '#/assessments/new',
  [AppStep.UPLOAD_ESSAY_DETAILS]: '#/assessments/upload',
  [AppStep.PROCESSING]: '#/processing',
  [AppStep.SHOW_RESULTS]: '#/results',
  [AppStep.ADMIN_DASHBOARD]: '#/admin',
  [AppStep.ACCOUNT]: '#/account',
  [AppStep.ABOUT]: '#/about',
  [AppStep.HELP_CONTACT]: '#/help',
  [AppStep.CONTACT_US]: '#/contact',
  [AppStep.PRIVACY_POLICY]: '#/privacy',
  [AppStep.REFUND_POLICY]: '#/refund',
  [AppStep.TERMS_OF_SERVICE]: '#/terms',
};

const HASH_TO_STEP: Record<string, AppStep> = Object.entries(STEP_TO_HASH).reduce((acc, [step, hash]) => {
  acc[hash] = step as AppStep;
  return acc;
}, {} as Record<string, AppStep>);

const AUTH_REQUIRED_STEPS: Set<AppStep> = new Set([
  AppStep.CHOOSE_ASSESSMENT,
  AppStep.CREATE_ASSESSMENT,
  AppStep.UPLOAD_ESSAY_DETAILS,
  AppStep.PROCESSING,
  AppStep.SHOW_RESULTS,
  AppStep.ADMIN_DASHBOARD,
  AppStep.ACCOUNT,
]);

// Public pages that can be accessed without authentication
const PUBLIC_STEPS: Set<AppStep> = new Set([
  AppStep.HOME,
  AppStep.AUTH,
  AppStep.ABOUT,
  AppStep.HELP_CONTACT,
  AppStep.CONTACT_US,
  AppStep.PRIVACY_POLICY,
  AppStep.REFUND_POLICY,
  AppStep.TERMS_OF_SERVICE,
]);


const initialEssayDataState: Partial<Essay> = {
  id: '',
  studentName: '',
  assessmentId: '',
  uploadedImages: [],
  transcribedText: null,
  modelResults: {},
  manualMarkingResults: [],
  manualOverallScore: null,
  teacherFeedback: '',
  varianceExplanation: null,
  inputType: null,
  // Legacy fields for backward compatibility
  rawMarkingRuns: [], 
  consolidatedMarkingResults: [], 
  finalOverallScore: null,
  finalOverallBand: null,
  finalConfidence: null,
  ensembleSpread: null,
  feedback: null,
  markingPromptUsed: null,
  justificationGenerationPromptUsed: null,
  metaAnalysisPromptUsed: null,
  modelUsed: null,
  scoringMethodUsed: null,
  scoringCalculationTrace: null,
};

const sortImagesByPageLabel = (images: Array<{pageLabel: string; file: File; base64: string; id: string}>) => {
  const extractPageNumber = (label: string) => {
    const cleanedLabel = String(label).toLowerCase().replace('page', '').trim();
    const num = parseInt(cleanedLabel, 10);
    return isNaN(num) ? Infinity : num;
  };

  return [...images].sort((a, b) => {
    const pageNumA = extractPageNumber(a.pageLabel);
    const pageNumB = extractPageNumber(b.pageLabel);

    if (pageNumA !== Infinity && pageNumB !== Infinity) {
      if (pageNumA !== pageNumB) return pageNumA - pageNumB;
      return a.pageLabel.localeCompare(b.pageLabel);
    }
    if (pageNumA !== Infinity) return -1; 
    if (pageNumB !== Infinity) return 1;  
    return a.pageLabel.localeCompare(b.pageLabel); 
  });
};


const App = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentStep, setCurrentStep] = useState<AppStep>(AppStep.HOME);
  const [activeEssayData, setActiveEssayData] = useState<Partial<Essay>>(initialEssayDataState);
  
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [viewingAssessment, setViewingAssessment] = useState<Assessment | null>(null);
  const [essaysForViewingAssessment, setEssaysForViewingAssessment] = useState<Essay[]>([]);

  const [selectedAssessmentForEdit, setSelectedAssessmentForEdit] = useState<Assessment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingMessage, setProcessingMessage] = useState('Processing, please wait...');
  const [activeProcessingContext, setActiveProcessingContext] = useState<'marking' | 'remark' | null>(null);
  const [showApiKeyHelp, setShowApiKeyHelp] = useState(false);
  const [showQuotaHelp, setShowQuotaHelp] = useState(false);
  const [showDomainAuthHelp, setShowDomainAuthHelp] = useState(false);
  const [showStorageRulesHelp, setShowStorageRulesHelp] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState(DEFAULT_MODEL_ID);
  const [showMarkingModelsModal, setShowMarkingModelsModal] = useState(false);
  const [showCreditConfirmationModal, setShowCreditConfirmationModal] = useState(false);
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [creditConfirmationData, setCreditConfirmationData] = useState<{
    creditsRequired: number;
    actionDescription: string;
    onConfirm: () => void;
  } | null>(null);
  const [pendingEssaySubmission, setPendingEssaySubmission] = useState<{studentName: string, essayInput: any} | null>(null);
  const [pendingEssayRemark, setPendingEssayRemark] = useState<Essay | null>(null);
  const [essaySortOrder, setEssaySortOrder] = useState('manual-desc');
  const [selectedUploadPath, setSelectedUploadPath] = useState<'single' | 'batch' | null>(null);
  const [singleUploadMethod, setSingleUploadMethod] = useState<'image' | 'document' | 'text' | null>(null);
  const [selectedBatchOption, setSelectedBatchOption] = useState<'pdfs' | 'documents' | null>(null);
  const [showingArchived, setShowingArchived] = useState(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const { manualScoringEnabled, hydratePreferences, reset: resetPreferences } = usePreferencesStore();
  const resetUploadFlow = useCallback(() => {
    setSelectedUploadPath(null);
    setSingleUploadMethod(null);
    setSelectedBatchOption(null);
  }, []);
  const goToAllAssessments = useCallback(() => {
    resetUploadFlow();
    setViewingAssessment(null);
    setActiveProcessingContext(null);
    setCurrentStep(AppStep.CHOOSE_ASSESSMENT);
  }, [resetUploadFlow]);
  const goToAssessmentDetails = useCallback(() => {
    resetUploadFlow();
    setActiveProcessingContext(null);
    setCurrentStep(AppStep.CHOOSE_ASSESSMENT);
  }, [resetUploadFlow]);
  const handleContactNavigate = useCallback((step: AppStep) => {
    if (!currentUser && step === AppStep.CHOOSE_ASSESSMENT) {
      setCurrentStep(AppStep.HOME);
      return;
    }
    setCurrentStep(step);
  }, [currentUser]);
  
  const [confirmModalState, setConfirmModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmButtonText: 'Confirm Delete',
    loadingText: 'Deleting...',
    onConfirm: () => {},
  });

  const uploadProgressConfig = useMemo(() => {
    const steps = [
      { id: 'choose-path', name: 'Upload type' },
      { id: 'method', name: selectedUploadPath === 'batch' ? 'File type' : 'Essay format' },
      { id: 'details', name: selectedUploadPath === 'batch' ? 'Attach files' : 'Upload essay' },
      { id: 'marking', name: 'Marking' },
      { id: 'results', name: 'Results' },
    ];

    let current = 'choose-path';
    if (currentStep === AppStep.SHOW_RESULTS) {
      current = 'results';
    } else if (currentStep === AppStep.PROCESSING) {
      current = 'marking';
    } else if (selectedUploadPath === 'single') {
      current = singleUploadMethod ? 'details' : 'method';
    } else if (selectedUploadPath === 'batch') {
      current = selectedBatchOption ? 'details' : 'method';
    }

    return { steps, current };
  }, [selectedUploadPath, singleUploadMethod, selectedBatchOption, currentStep]);

  const remarkProgressConfig = useMemo(() => {
    const steps = [
      { id: 'queue', name: 'Queued' },
      { id: 'marking', name: 'Re-marking' },
      { id: 'saving', name: 'Saving' },
      { id: 'results', name: 'Updated results' },
    ];

    let current = 'marking';
    const normalizedMessage = processingMessage.toLowerCase();
    if (currentStep === AppStep.SHOW_RESULTS) {
      current = 'results';
    } else if (normalizedMessage.includes('refresh') || normalizedMessage.includes('saving')) {
      current = 'saving';
    }

    return { steps, current };
  }, [processingMessage, currentStep]);

  const openConfirmModal = ({ title, message, confirmButtonText = 'Confirm Delete', loadingText = 'Deleting...', onConfirm }: {title: string, message: string, confirmButtonText?: string, loadingText?: string, onConfirm: () => Promise<void>}) => {
    setConfirmModalState({
      isOpen: true,
      title,
      message,
      confirmButtonText,
      loadingText,
      onConfirm: async () => {
        setIsLoading(true);
        await onConfirm();
        closeConfirmModal();
        setIsLoading(false);
      },
    });
  };

  const closeConfirmModal = () => {
    setConfirmModalState({ isOpen: false, title: '', message: '', confirmButtonText: 'Confirm Delete', loadingText: 'Deleting...', onConfirm: () => {} });
  };

  // Handle hash-based routing with proper auth state awareness
  useEffect(() => {
    // Don't process routing until auth state is initialized
    if (!isAuthInitialized) return;

    const normalizeHash = (hash: string) => (hash && hash.startsWith('#') ? hash : '#/');

    const applyHash = (hash: string) => {
      const normalizedHash = normalizeHash(hash);
      const targetStep = HASH_TO_STEP[normalizedHash] || AppStep.HOME;
      
      // If a signed-in user ends up on the auth hash, redirect them to the dashboard
      if (currentUser && targetStep === AppStep.AUTH) {
        setCurrentStep(AppStep.CHOOSE_ASSESSMENT);
        return;
      }
      
      // Only redirect to home if the step requires auth AND user is not logged in
      if (!currentUser && AUTH_REQUIRED_STEPS.has(targetStep)) {
        setCurrentStep(AppStep.HOME);
        return;
      }
      
      setCurrentStep(targetStep);
    };

    applyHash(window.location.hash || '#/');

    const handleHashChange = () => applyHash(window.location.hash || '#/');
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [currentUser, isAuthInitialized]);

  // Update URL hash when step changes
  useEffect(() => {
    const targetHash = STEP_TO_HASH[currentStep] || '#/';
    if ((window.location.hash || '#/') !== targetHash) {
      window.history.replaceState(null, '', targetHash);
    }
  }, [currentStep]);

  // Update canonical tag and page title based on current step
  useEffect(() => {
    const baseUrl = 'https://aimarkermate.com';
    
    // Map hash routes to clean URLs for canonical tags (without hash symbols)
    const hashToCleanPath: Record<string, string> = {
      '#/': '',
      '#/auth': 'auth',
      '#/assessments': 'assessments',
      '#/assessments/new': 'assessments/new',
      '#/assessments/upload': 'assessments/upload',
      '#/processing': 'processing',
      '#/results': 'results',
      '#/admin': 'admin',
      '#/account': 'account',
      '#/about': 'about',
      '#/help': 'help',
      '#/contact': 'contact',
      '#/privacy': 'privacy',
      '#/refund': 'refund',
      '#/terms': 'terms',
    };
    
    const currentHash = STEP_TO_HASH[currentStep] || '#/';
    const cleanPath = hashToCleanPath[currentHash] || '';
    const canonicalUrl = cleanPath ? `${baseUrl}/${cleanPath}` : baseUrl;
    
    // Update or create canonical tag
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;
    
    // Update page title based on current step
    const pageTitles: Partial<Record<AppStep, string>> = {
      [AppStep.HOME]: 'AI Marker Mate - AI-Powered Essay Marking Assistant',
      [AppStep.ABOUT]: 'About - AI Marker Mate',
      [AppStep.PRIVACY_POLICY]: 'Privacy Policy - AI Marker Mate',
      [AppStep.REFUND_POLICY]: 'Refund Policy - AI Marker Mate',
      [AppStep.TERMS_OF_SERVICE]: 'Terms of Service - AI Marker Mate',
      [AppStep.CONTACT_US]: 'Contact Us - AI Marker Mate',
      [AppStep.HELP_CONTACT]: 'Help & Support - AI Marker Mate',
      [AppStep.ACCOUNT]: 'My Account - AI Marker Mate',
      [AppStep.CHOOSE_ASSESSMENT]: 'Assessments - AI Marker Mate',
      [AppStep.CREATE_ASSESSMENT]: 'Create Assessment - AI Marker Mate',
    };
    
    document.title = pageTitles[currentStep] || 'AI Marker Mate';
    
    // Update meta description for public pages
    let metaDescription = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    
    const pageDescriptions: Partial<Record<AppStep, string>> = {
      [AppStep.HOME]: 'AI Marker Mate - Professional AI-powered essay marking assistant for teachers. Save time with automated marking, detailed feedback, and comprehensive rubric analysis.',
      [AppStep.ABOUT]: 'Learn about AI Marker Mate, the AI-powered essay marking assistant designed to help teachers save time while providing quality feedback to students.',
      [AppStep.PRIVACY_POLICY]: 'Read our privacy policy to understand how AI Marker Mate protects your data and respects your privacy.',
      [AppStep.REFUND_POLICY]: 'Review our refund policy for AI Marker Mate credit purchases and subscriptions.',
      [AppStep.TERMS_OF_SERVICE]: 'Read the terms of service for using AI Marker Mate, including user responsibilities and service guidelines.',
      [AppStep.CONTACT_US]: 'Get in touch with the AI Marker Mate team. We\'re here to help with any questions or concerns.',
      [AppStep.HELP_CONTACT]: 'Find answers to common questions and get support for using AI Marker Mate.',
    };
    
    metaDescription.content = pageDescriptions[currentStep] || 'AI Marker Mate - AI-powered essay marking assistant for teachers.';
  }, [currentStep]);

  // Fetch credit balance when user changes
  useEffect(() => {
    if (currentUser) {
      const fetchBalance = async () => {
        try {
          const balance = await getCreditBalance(currentUser.uid, currentUser.email || undefined);
          setCreditBalance(balance);
        } catch (err) {
          console.warn('Failed to fetch credit balance:', err);
        }
      };
      fetchBalance();
    } else {
      setCreditBalance(0);
    }
  }, [currentUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsLoading(true);
      if (user) {
        setCurrentUser(user);
        const isAdminUser = !!(user.email && ADMIN_USER_EMAILS.includes(user.email));
        setIsAdmin(isAdminUser);
        hydratePreferences(user.uid, isAdminUser);
        
        // Assign numbers to existing assessments that don't have them
        try {
          await dataService.assignNumbersToExistingAssessments(user.uid);
        } catch (error) {
          console.warn('Failed to assign numbers to existing assessments:', error);
        }
        
        await refreshAssessments(user.uid);
        
        // Only navigate to assessments if we're not on a public page
        const currentHash = window.location.hash || '#/';
        const currentStepFromHash = HASH_TO_STEP[currentHash];
        if (!currentStepFromHash || !PUBLIC_STEPS.has(currentStepFromHash)) {
          setCurrentStep(AppStep.CHOOSE_ASSESSMENT);
        }
      } else {
        setCurrentUser(null);
        setIsAdmin(false);
        resetPreferences();
        setAssessments([]);
        setViewingAssessment(null);
        setEssaysForViewingAssessment([]);
        setActiveEssayData(initialEssayDataState);
        
        // Don't automatically redirect to home if we're on a public page
        const currentHash = window.location.hash || '#/';
        const currentStepFromHash = HASH_TO_STEP[currentHash];
        if (!currentStepFromHash || !PUBLIC_STEPS.has(currentStepFromHash)) {
          setCurrentStep(AppStep.HOME);
        }
      }
      setIsLoading(false);
      setIsAuthInitialized(true); // Mark auth as initialized
    });
    return () => unsubscribe();
  }, [hydratePreferences, resetPreferences]);

  const handleGlobalError = (error: any) => {
    console.error("A global error was caught:", error);
    // Firebase errors have a `code` property, which is more reliable than parsing messages.
    const errorCode = error?.code;
    const errorMessage = error?.message || 'An unknown error occurred.';

    if (errorMessage.includes('API_KEY_INVALID')) {
        setShowApiKeyHelp(true);
        setError(null);
    } else if (errorMessage === QUOTA_EXCEEDED_USER_MESSAGE) {
        setShowQuotaHelp(true);
        setError(null);
    } else if (errorCode === "storage/unauthorized") { // More robust check
        setError("Firebase Storage permission denied. Your security rules might be incorrect.");
        setShowStorageRulesHelp(true);
    } else {
        setError(errorMessage);
    }
    // Stop any loading spinners
    setIsLoading(false);
  };

  const runMarkingPipeline = async (essayId: string, transcribedText: string, assessment: Assessment, modelId: string, updateStatus: (message: string) => void, essayInputType: string) => {
    const modelConfig = MODELS[modelId];

    updateStatus("Cleaning essay text...");
    const cleanedText = stripTeacherComments(transcribedText);

    updateStatus(`Launching marking run with ${modelConfig.name}...`);
    const systemInstructionsToUse = assessment.systemInstructions ?? DEFAULT_SYSTEM_INSTRUCTIONS;

    const markingPromises = [];
    for (let i = 0; i < NUMBER_OF_MARKING_RUNS; i++) {
        const runStatusCallback = (message: string) => {
            updateStatus(`Run ${i + 1}/${NUMBER_OF_MARKING_RUNS}: ${message}`);
        };
        
        markingPromises.push(
            markEssay(cleanedText, systemInstructionsToUse, assessment.rubric, assessment.question, assessment.yearLevel, modelConfig, assessment.exemplars, essayInputType, assessment.assessmentType, assessment.examCondition, assessment.minWordCount, assessment.maxWordCount, runStatusCallback)
        );
    }
    
    const results = await Promise.allSettled(markingPromises);
    const allMarkingRunsData: any[] = [];
    results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
            allMarkingRunsData.push({ ...result.value, runNumber: index + 1 });
        } else if (result.status === 'rejected') {
            console.error(`Marking run ${index + 1} failed:`, result.reason);
        }
    });

    if (allMarkingRunsData.length === 0) {
        const firstErrorResult = results.find(r => r.status === 'rejected');
        let firstError = "All marking runs failed for an unknown reason.";
        if (firstErrorResult && firstErrorResult.status === 'rejected') {
            firstError = firstErrorResult.reason?.message || "All marking runs failed for an unknown reason.";
        }
        throw new Error(firstError);
    }

    updateStatus("Consolidating marking runs...");
    
    // Check for high variance and run meta-analysis if needed
    const initialConsolidation = dataService.consolidateMarkingRuns(allMarkingRunsData, assessment, cleanedText);
    const stdDev = (initialConsolidation && 
                   typeof initialConsolidation === 'object' && 
                   'ensembleSpread' in initialConsolidation && 
                   initialConsolidation.ensembleSpread && 
                   typeof initialConsolidation.ensembleSpread === 'object' &&
                   'stdDev' in initialConsolidation.ensembleSpread) 
                  ? Number(initialConsolidation.ensembleSpread.stdDev) : 0;
    const HIGH_VARIANCE_THRESHOLD = 2.0;
    let finalMarkingRuns = [...allMarkingRunsData];
    let metaAnalysisPromptUsed = null;

    if (allMarkingRunsData.length > 1 && stdDev > HIGH_VARIANCE_THRESHOLD) {
        updateStatus(`High variance detected (σ=${stdDev.toFixed(2)}). Running meta-analysis...`);
        try {
            const metaAnalysisResult = await runMetaAnalysis(
                cleanedText,
                assessment.rubric,
                assessment.question,
                assessment.yearLevel,
                allMarkingRunsData,
                modelConfig
            );
            finalMarkingRuns = [metaAnalysisResult];
            metaAnalysisPromptUsed = metaAnalysisResult.prompt;
        } catch (metaError) {
            console.warn("Meta-analysis failed. Proceeding with original high-variance results.", metaError);
            updateStatus("Meta-analysis failed. Using original results.");
        }
    }
    
    const consolidationResult = dataService.consolidateMarkingRuns(finalMarkingRuns, assessment, cleanedText);
    const { 
        consolidatedCriteria, 
        finalOverallScore, 
        finalOverallBand, 
        consolidatedAuditData,
        scoringMethodUsed,
        scoringCalculationTrace,
    } = consolidationResult;
    
    // Safe access to optional properties
    const finalConfidence = 'finalConfidence' in consolidationResult ? (consolidationResult.finalConfidence as string | null) : null;
    const ensembleSpread = 'ensembleSpread' in consolidationResult ? (consolidationResult.ensembleSpread as { min: number; max: number; stdDev: number; } | null) : null;

    updateStatus("Generating justifications for scores...");
    const { justifications, prompt: justificationGenerationPromptUsed } = await generateConsolidatedJustifications(
        cleanedText, 
        assessment.rubric, 
        assessment.question, 
        consolidatedCriteria, 
        finalMarkingRuns, 
        assessment.yearLevel, 
        modelConfig
    );
    const criteriaWithJustifications = dataService.mergeJustifications(consolidatedCriteria, justifications);
    
    updateStatus("Generating final feedback...");
    const feedback = await generateFeedback(
        cleanedText, 
        systemInstructionsToUse, 
        assessment.rubric, 
        assessment.question, 
        criteriaWithJustifications, 
        finalOverallScore || 0, 
        finalOverallBand || 'Not Determined', 
        finalMarkingRuns, 
        assessment.yearLevel, 
        modelConfig,
        consolidatedAuditData
    );

    updateStatus("Saving final results...");

    // NEW: Store results per model instead of overwriting everything
    const modelResult: ModelResult = {
        modelId: modelConfig.id,
        modelName: modelConfig.name,
        modelVersion: modelConfig.version,
        promptVersion: MARKING_PROMPT_VERSION,
        lastRunAt: new Date(), // Keep as JS Date - will be converted to serverTimestamp in updateEssayWithModelResult
        timestamp: new Date(), // Required field for ModelResult interface
        rawMarkingRuns: finalMarkingRuns.map(({prompt, ...rest}) => rest),
        consolidatedMarkingResults: criteriaWithJustifications,
        finalOverallScore,
        finalOverallBand,
        finalConfidence,
        ensembleSpread,
        feedback,
        markingPromptUsed: allMarkingRunsData[0]?.prompt || null,
        justificationGenerationPromptUsed,
        metaAnalysisPromptUsed,
        scoringMethodUsed,
        scoringCalculationTrace,
    };

    await dataService.updateEssayWithModelResult(essayId, modelResult);
    
    // Update the active essay data to show the latest model's results
    setActiveEssayData(prev => ({
        ...prev,
        modelResults: {
            ...prev.modelResults,
            [modelConfig.id]: modelResult
        },
        transcribedText: cleanedText,
        // Keep legacy fields for backward compatibility with current UI
        rawMarkingRuns: modelResult.rawMarkingRuns,
        consolidatedMarkingResults: modelResult.consolidatedMarkingResults,
        finalOverallScore: modelResult.finalOverallScore,
        finalOverallBand: modelResult.finalOverallBand,
        finalConfidence: modelResult.finalConfidence,
        ensembleSpread: modelResult.ensembleSpread,
        feedback: modelResult.feedback,
        markingPromptUsed: modelResult.markingPromptUsed,
        justificationGenerationPromptUsed: modelResult.justificationGenerationPromptUsed,
        metaAnalysisPromptUsed: modelResult.metaAnalysisPromptUsed,
        modelUsed: modelResult.modelName,
        scoringMethodUsed: modelResult.scoringMethodUsed,
        scoringCalculationTrace: modelResult.scoringCalculationTrace,
    }));
    
    updateStatus("Done!");
    return true; 
  };
  
  const processAndMarkEssay = async (studentName: string, essayInput: any) => {
    // Non-admins always use the default model without showing selection UI
    setPendingEssaySubmission({ studentName, essayInput });
    if (!isAdmin) {
      await handleModelSelectionConfirm([DEFAULT_MODEL_ID]);
      return;
    }

    // Admins can choose which models to run
    setShowMarkingModelsModal(true);
  };

  const handleModelSelectionConfirm = async (selectedModelIds: string[]) => {
    setShowMarkingModelsModal(false);
    
    if (!pendingEssaySubmission || !currentUser || !viewingAssessment) return;
    
    const { studentName, essayInput } = pendingEssaySubmission;
    
    // Show credit confirmation modal instead of immediately processing
    const totalCreditsNeeded = selectedModelIds.length; // 1 credit per model
    const actionDescription = `Mark essay for ${studentName} using ${selectedModelIds.length} model${selectedModelIds.length > 1 ? 's' : ''}`;
    
    handleCreditConfirmation(totalCreditsNeeded, actionDescription, () => {
      setPendingEssaySubmission(null);
      proceedWithEssayMarking(selectedModelIds, studentName, essayInput);
    });
  };

  const proceedWithEssayMarking = async (selectedModelIds: string[], studentName: string, essayInput: any) => {
    if (!currentUser || !viewingAssessment) {
      handleGlobalError(new Error('User or assessment not found'));
      return;
    }
    
    setError(null);
    setActiveProcessingContext('marking');
    setCurrentStep(AppStep.PROCESSING);

    let essayId: string | null = null;
    let transcribedText = "";
    const uploadedImages: Array<{id: string; pageLabel: string; previewUrl: string}> = [];
    const creditsConsumed: string[] = []; // Track consumed credit transaction IDs for potential refund

    try {
      const newEssay = await dataService.addEssay(currentUser.uid, viewingAssessment.id, studentName, essayInput.type);
      if (!newEssay) {
        throw new Error("Failed to create essay");
      }
      essayId = newEssay.id;
      
      setActiveEssayData({
        ...initialEssayDataState,
        id: essayId,
        studentName,
        assessmentId: viewingAssessment.id,
        inputType: essayInput.type,
      });

      switch (essayInput.type) {
        case 'images':
          const sortedImages = sortImagesByPageLabel(essayInput.data);
          for (let i = 0; i < sortedImages.length; i++) {
            const image = sortedImages[i];
            setProcessingMessage(`Uploading and transcribing page ${i + 1} of ${sortedImages.length}...`);
            const publicUrl = await dataService.uploadEssayImage(currentUser.uid, essayId, image.file);
            uploadedImages.push({ 
                id: image.id, 
                pageLabel: image.pageLabel, 
                previewUrl: publicUrl
            });
            // Use the first selected model for transcription
            const pageText = await transcribeImage(image.base64, selectedModelIds[0]);
            transcribedText += `--- Page ${image.pageLabel} ---\n${pageText}\n\n`;
          }
          break;
        case 'document':
           setProcessingMessage(`Extracting text from document(s)...`);
          for (const file of essayInput.data) {
             transcribedText += `--- From file: ${file.name} ---\n${await extractTextFromFile(file)}\n\n`;
          }
          break;
        case 'text':
          transcribedText = essayInput.data;
          break;
      }
      
      transcribedText = transcribedText.trim();
      if (!transcribedText) {
          throw new Error("Failed to extract any text from the provided input.");
      }

      // Calculate word count for the essay
      setProcessingMessage("Calculating word count...");
      const wordCount = calculateWordCount(transcribedText);

      await dataService.updateEssay(essayId, { transcribedText, uploadedImages, wordCount });
      setActiveEssayData((prev: Partial<Essay>) => ({ ...prev, transcribedText, uploadedImages, wordCount }));
      
      // Run marking pipeline for each selected model and consume credits
      for (let i = 0; i < selectedModelIds.length; i++) {
        const modelId = selectedModelIds[i];
        const modelName = MODELS[modelId]?.name || modelId;
        
        if (selectedModelIds.length > 1) {
          setProcessingMessage(`Running AI model (${i + 1} of ${selectedModelIds.length})...`);
        }
        
        // Consume credits before marking each model
        setProcessingMessage(`Consuming credits for AI model...`);
        const creditTransaction = await consumeCredits(
          currentUser.uid,
          1, // 1 credit per essay marking
          'essay',
          essayId,
          `Essay marking with ${modelName} for student: ${studentName}`,
          currentUser.email || undefined
        );
        creditsConsumed.push(creditTransaction.id);
        
        await runMarkingPipeline(essayId, transcribedText, viewingAssessment, modelId, setProcessingMessage, essayInput.type);
      }
      
      setProcessingMessage("Essay marking completed successfully!");
      setCurrentStep(AppStep.SHOW_RESULTS);
    
    } catch (err) {
      // Refund credits if the marking process failed
      if (creditsConsumed.length > 0) {
        try {
          setProcessingMessage("Refunding credits due to processing error...");
          for (const transactionId of creditsConsumed) {
            await refundCredits(
              currentUser.uid,
              1,
              'essay',
              essayId || 'unknown',
              `Refund for failed essay marking. Original transaction: ${transactionId}`,
              currentUser.email || undefined
            );
          }
        } catch (refundError) {
          console.error('Failed to refund credits:', refundError);
        }
      }
      
      handleGlobalError(err);
      setCurrentStep(AppStep.UPLOAD_ESSAY_DETAILS);
    }
  };

  const handleModelSelectionCancel = () => {
    setShowMarkingModelsModal(false);
    setPendingEssaySubmission(null);
  };

  // Credit purchase handlers
  const handlePurchaseCredits = async (priceId: string) => {
    if (!currentUser) return;
    
    try {
      const { createCheckoutSession } = await import('./services/stripeService');
      const { sessionId, url } = await createCheckoutSession(
        priceId,
        currentUser.uid,
        currentUser.email || '',
        `${window.location.origin}?purchase=success`,
        `${window.location.origin}?purchase=cancelled`
      );
      
      // Redirect to Stripe checkout
      window.location.href = url;
    } catch (error) {
      handleGlobalError(error);
    }
  };

  const handleCreditConfirmation = (creditsRequired: number, actionDescription: string, onConfirm: () => void) => {
    setCreditConfirmationData({
      creditsRequired,
      actionDescription,
      onConfirm
    });
    setShowCreditConfirmationModal(true);
  };

  const handleCreditConfirmationClose = () => {
    setShowCreditConfirmationModal(false);
    setCreditConfirmationData(null);
  };

  const handleCreditConfirmationConfirm = () => {
    if (creditConfirmationData) {
      setShowCreditConfirmationModal(false);
      creditConfirmationData.onConfirm();
      setCreditConfirmationData(null);
    }
  };

  const handleShowCreditPurchaseModal = () => {
    setShowCreditPurchaseModal(true);
  };

  const handleCreditPurchaseModalClose = () => {
    setShowCreditPurchaseModal(false);
  };
  
  const refreshAssessments = async (userId: string) => {
    try {
      const assessmentsFromDb = await dataService.getAssessments(userId, showingArchived);
      setAssessments(assessmentsFromDb || []);
    } catch(err) {
      handleGlobalError(err);
    }
  };

  const handleToggleArchiveView = async () => {
    const newShowingArchived = !showingArchived;
    setShowingArchived(newShowingArchived);
    if (currentUser) {
      try {
        const assessmentsFromDb = await dataService.getAssessments(currentUser.uid, newShowingArchived);
        setAssessments(assessmentsFromDb || []);
      } catch(err) {
        handleGlobalError(err);
      }
    }
  };

  const handleUnarchiveAssessment = async (assessmentId: string, assessmentName: string) => {
    openConfirmModal({
      title: 'Restore Assessment',
      message: `Are you sure you want to restore "${assessmentName}"? This will make it visible in the main assessments list again.`,
      confirmButtonText: 'Restore',
      loadingText: 'Restoring...',
      onConfirm: async () => {
        try {
          await dataService.archiveAssessment(assessmentId, false);
          if (currentUser) {
            const assessmentsFromDb = await dataService.getAssessments(currentUser.uid, showingArchived);
            setAssessments(assessmentsFromDb || []);
          }
        } catch (err) {
          handleGlobalError(err);
        }
      },
    });
  };

  const handleAuthError = (error: any) => {
    const errorMessage = error?.message?.toLowerCase() || '';
    if (errorMessage.includes('requests from referer') && errorMessage.includes('are blocked')) {
      setShowDomainAuthHelp(true);
    } else {
      console.error("Caught unhandled auth error:", error);
    }
  };
  
  const handleSaveAssessment = async (assessmentData: any) => {
    setIsLoading(true);
    try {
      if (assessmentData.id) {
        await dataService.updateAssessment(assessmentData);
      } else {
        await dataService.addAssessment(assessmentData, currentUser!.uid);
      }
      await refreshAssessments(currentUser!.uid);
      setCurrentStep(AppStep.CHOOSE_ASSESSMENT);
      setSelectedAssessmentForEdit(null);
    } catch (err) {
      handleGlobalError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAssessment = async (assessmentId: string, assessmentName: string) => {
    openConfirmModal({
      title: 'Delete Assessment',
      message: `Are you sure you want to delete "${assessmentName}"? This will also delete all associated essays. This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await dataService.deleteAssessment(assessmentId, currentUser!.uid);
          await refreshAssessments(currentUser!.uid);
        } catch (err) {
          handleGlobalError(err);
        }
      },
    });
  };

  const handleArchiveAssessment = async (assessmentId: string, assessmentName: string) => {
    openConfirmModal({
      title: 'Archive Assessment',
      message: `Are you sure you want to archive "${assessmentName}"? This will hide it from the main list, but it can be restored later.`,
      confirmButtonText: 'Archive',
      loadingText: 'Archiving...',
      onConfirm: async () => {
        try {
          await dataService.archiveAssessment(assessmentId, true);
          await refreshAssessments(currentUser!.uid);
        } catch (err) {
          handleGlobalError(err);
        }
      },
    });
  };

  const handleDeleteEssay = async (essayId: string, studentName: string) => {
    openConfirmModal({
      title: 'Delete Essay',
      message: `Are you sure you want to delete the essay for "${studentName}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await dataService.deleteEssay(essayId);
          if (currentUser && viewingAssessment) {
            const updatedEssays = await dataService.getEssaysForAssessment(currentUser.uid, viewingAssessment.id);
            setEssaysForViewingAssessment(updatedEssays || []);
          }
        } catch (err) {
          handleGlobalError(err);
        }
      },
    });
  };

  const handleBatchDeleteEssays = async (essayIds: string[], count: number) => {
    if (!essayIds || essayIds.length === 0) return;
    openConfirmModal({
      title: count > 1 ? 'Delete Essays' : 'Delete Essay',
      message: `Are you sure you want to delete ${count} selected essay${count !== 1 ? 's' : ''}? This action cannot be undone.`,
      confirmButtonText: 'Delete',
      loadingText: 'Deleting...',
      onConfirm: async () => {
        try {
          await Promise.all(essayIds.map((id) => dataService.deleteEssay(id)));
          if (currentUser && viewingAssessment) {
            const updatedEssays = await dataService.getEssaysForAssessment(currentUser.uid, viewingAssessment.id);
            setEssaysForViewingAssessment(updatedEssays || []);
          }
        } catch (err) {
          handleGlobalError(err);
        }
      },
    });
  };

  const handleStartOver = () => {
    setActiveEssayData(initialEssayDataState);
    setViewingAssessment(null);
    setSelectedAssessmentForEdit(null);
    setActiveProcessingContext(null);
    setCurrentStep(AppStep.CHOOSE_ASSESSMENT);
    setError(null);
  };

  const handleRemark = async (essayToRemark: Essay, onBatchStatusUpdate?: (message: string) => void, selectedModelIds?: string[]) => {
    // If this is not a batch operation, show credit confirmation modal first
    if (!onBatchStatusUpdate) {
      const modelsToUse = selectedModelIds && selectedModelIds.length > 0 ? selectedModelIds : [selectedModelId];
      const totalCreditsNeeded = modelsToUse.length; // 1 credit per model
      const actionDescription = `Remark essay for ${essayToRemark.studentName} using ${modelsToUse.length} model${modelsToUse.length > 1 ? 's' : ''}`;
      
      handleCreditConfirmation(totalCreditsNeeded, actionDescription, () => {
        proceedWithRemarkEssay(essayToRemark, onBatchStatusUpdate, selectedModelIds);
      });
      return;
    }
    
    // For batch operations, proceed directly (they handle their own credit checking)
    return proceedWithRemarkEssay(essayToRemark, onBatchStatusUpdate, selectedModelIds);
  };

  const proceedWithRemarkEssay = async (essayToRemark: Essay, onBatchStatusUpdate?: (message: string) => void, selectedModelIds?: string[]) => {
    const updateStatus = onBatchStatusUpdate || setProcessingMessage;
    
    console.log(`[DEBUG] Starting handleRemark for essay ${essayToRemark.id} (${essayToRemark.studentName}):`, {
      selectedModelIds,
      isBatchMode: !!onBatchStatusUpdate,
      viewingAssessmentId: viewingAssessment?.id
    });
    
    if (!onBatchStatusUpdate) {
       setError(null);
       setActiveProcessingContext('remark');
       setCurrentStep(AppStep.PROCESSING);
    }
   
    try {
      const essayData = await dataService.getEssay(essayToRemark.id) as Essay;
      console.log(`[DEBUG] Retrieved essay data for ${essayToRemark.id}:`, {
        hasTranscribedText: !!essayData?.transcribedText,
        currentModelResults: Object.keys(essayData?.modelResults || {}),
        assessmentId: essayData?.assessmentId
      });
      
      if (!essayData || !essayData.transcribedText) {
          throw new Error("Could not find essay data or transcribed text to re-mark.");
      }

      const assessment = assessments.find(a => a.id === essayData.assessmentId);
      if (!assessment) {
          throw new Error("Could not find the parent assessment for this essay.");
      }
      
      setActiveEssayData(essayData as Partial<Essay>);
      
      // Use provided model IDs or fall back to default model for backward compatibility
      const modelsToUse = selectedModelIds && selectedModelIds.length > 0 ? selectedModelIds : [selectedModelId];
      console.log(`[DEBUG] Models to use for remarking:`, modelsToUse);

      // For batch operations, check credit balance before starting remarking (unless admin user)
      if (currentUser && onBatchStatusUpdate) {
        try {
          updateStatus("Checking credit balance...");
          const totalCreditsNeeded = await estimateCreditsForJob('essay_remarking', modelsToUse.length);
          const hasSufficient = await hasSufficientCredits(currentUser.uid, totalCreditsNeeded, currentUser.email || undefined);
          
          if (!hasSufficient) {
            const currentBalance = await getCreditBalance(currentUser.uid, currentUser.email || undefined);
            throw new Error(`Insufficient credits for remarking. You need ${totalCreditsNeeded} credits but only have ${currentBalance}. Please purchase more credits to continue.`);
          }
        } catch (err: any) {
          if (err.message.includes('Insufficient credits')) {
            throw err; // Re-throw credit errors
          }
          // For other errors (like network issues), log and continue
          console.warn('Credit check failed, but continuing with remarking:', err);
        }
      }
      
      // Run marking pipeline for each selected model with enhanced retry logic and credit consumption
      let successfulModels = 0;
      const creditsConsumed: string[] = []; // Track consumed credit transaction IDs for potential refund
      
      for (let i = 0; i < modelsToUse.length; i++) {
        const modelId = modelsToUse[i];
        const modelName = MODELS[modelId]?.name || modelId;
        
        console.log(`[DEBUG] Starting model ${i + 1}/${modelsToUse.length}: ${modelName} (${modelId})`);
        
        if (modelsToUse.length > 1) {
          updateStatus(`Running AI model (${i + 1} of ${modelsToUse.length})...`);
        }
        
        // Consume credits before remarking each model (unless admin)
        if (currentUser) {
          try {
            updateStatus(`Consuming credits for AI model...`);
            const creditTransaction = await consumeCredits(
              currentUser.uid,
              1, // 1 credit per essay remarking
              'essay',
              essayData.id,
              `Essay remarking for student: ${essayData.studentName}`,
              currentUser.email || undefined
            );
            creditsConsumed.push(creditTransaction.id);
          } catch (creditError) {
            console.error(`Failed to consume credits for ${modelName}:`, creditError);
            // Don't fail the entire process for credit consumption errors if user is admin
            if (!isAdmin) {
              throw creditError;
            }
          }
        }
        
        // Enhanced retry logic for individual models
        let retryCount = 0;
        const maxRetries = 2; // Will try up to 3 times total (initial + 2 retries)
        let success = false;
        
        while (!success && retryCount <= maxRetries) {
          try {
            const retryText = retryCount > 0 ? ` (Retry ${retryCount})` : '';
            updateStatus(`Running AI model${retryText}...`);
            
            console.log(`[DEBUG] Calling runMarkingPipeline for ${modelName}, attempt ${retryCount + 1}`);
            await runMarkingPipeline(essayData.id, essayData.transcribedText, assessment, modelId, updateStatus, essayData.inputType || 'text');
            console.log(`[DEBUG] Successfully completed runMarkingPipeline for ${modelName}`);
            success = true;
            successfulModels++;
          } catch (modelError) {
            retryCount++;
            console.warn(`[DEBUG] Model ${modelName} failed on attempt ${retryCount}:`, modelError);
            
            if (retryCount <= maxRetries) {
              updateStatus(`AI model run failed, retrying (${retryCount}/${maxRetries})...`);
              // Small delay before retry
              await new Promise(resolve => setTimeout(resolve, 1000));
            } else {
              console.error(`[DEBUG] Model ${modelName} failed after ${maxRetries + 1} attempts:`, modelError);
              
              // Refund credits for failed model if not admin
              if (currentUser && !isAdmin && creditsConsumed.length > i) {
                try {
                  updateStatus(`Refunding credits for failed AI model run...`);
                  await refundCredits(
                    currentUser.uid,
                    1,
                    'essay',
                    essayData.id,
                    `Refund for failed essay remarking. Original transaction: ${creditsConsumed[i]}`,
                    currentUser.email || undefined
                  );
                } catch (refundError) {
                  console.error('Failed to refund credits for failed model:', refundError);
                }
              }
              
              // Continue with other models even if this one fails
              updateStatus(`AI model failed after ${maxRetries + 1} attempts, continuing...`);
            }
          }
        }
      }
      
      console.log(`[DEBUG] Completed remarking for essay ${essayToRemark.id}. Successful models: ${successfulModels}/${modelsToUse.length}`);
      
      if (!onBatchStatusUpdate) {
        setCurrentStep(AppStep.SHOW_RESULTS);
      } else {
        // For batch operations, refresh the essays list after completion
        console.log(`[DEBUG] Batch mode - refreshing essays list for assessment ${viewingAssessment?.id}`);
        if (currentUser && viewingAssessment) {
          try {
            updateStatus('Refreshing essay list...');
            const updatedEssays = await dataService.getEssaysForAssessment(currentUser.uid, viewingAssessment.id) as Essay[];
            console.log(`[DEBUG] Retrieved ${updatedEssays?.length || 0} essays from database after remarking`);
            
            // Find the updated essay and log its model results
            const updatedEssay = updatedEssays?.find(e => e.id === essayToRemark.id);
            if (updatedEssay) {
              console.log(`[DEBUG] Updated essay ${essayToRemark.id} now has model results:`, Object.keys((updatedEssay as Essay).modelResults || {}));
            } else {
              console.warn(`[DEBUG] Could not find updated essay ${essayToRemark.id} in refreshed list`);
            }
            
            setEssaysForViewingAssessment(updatedEssays || []);
          } catch (err) {
            console.warn('Failed to refresh essays list after batch remarking:', err);
          }
        }
      }
      
      // Verify the essay was actually updated by checking it directly from the database
      if (onBatchStatusUpdate) {
        try {
          const verificationEssay = await dataService.getEssay(essayToRemark.id);
          console.log(`[DEBUG] Verification check for essay ${essayToRemark.id}:`, {
            hasModelResults: !!verificationEssay?.modelResults,
            modelResultKeys: Object.keys(verificationEssay?.modelResults || {}),
            lastMarkedAt: verificationEssay?.markedAt
          });
        } catch (verifyError) {
          console.error(`[DEBUG] Failed to verify essay update:`, verifyError);
        }
      }
      
      return successfulModels > 0; // Return true if at least one model succeeded
    } catch (err) {
       console.error(`[DEBUG] Error in handleRemark for essay ${essayToRemark.id}:`, err);
       handleGlobalError(err);
       if (!onBatchStatusUpdate) {
          setCurrentStep(AppStep.SHOW_RESULTS);
       }
       return false;
    }
  };

  const handleSaveManualMarks = async (essayId: string, manualData: any) => {
    try {
      const payload = {
        manualMarkingResults: manualData.criteria,
        manualOverallScore: manualData.overallScore,
        teacherFeedback: manualData.teacherFeedback,
      };
      await dataService.updateEssay(essayId, payload);
      setActiveEssayData((prev: Partial<Essay>) => ({ ...prev, ...payload }));
      setEssaysForViewingAssessment((prev: Essay[]) => 
        prev.map(essay => essay.id === essayId ? { ...essay, ...payload } : essay)
      );
    } catch (err) {
      handleGlobalError(err);
    }
  };

  const handleExplainVariance = async (essayData: any, teacherFeedback: string, selectedModelForComparison?: string) => {
     setError(null);
     try {
        const assessment = assessments.find(a => a.id === essayData.assessmentId);
        if (!assessment) {
            throw new Error("Could not find assessment details to explain variance.");
        }
        
        const explanation = await explainMarkingVariance(essayData, assessment, teacherFeedback, MODELS[selectedModelId], selectedModelForComparison);
        
        const payload = { varianceExplanation: explanation };
        await dataService.updateEssay(essayData.id, payload);
        setActiveEssayData((prev: Partial<Essay>) => ({ ...prev, ...payload }));
     } catch (err) {
        handleGlobalError(err);
     }
  };

  const handleDeleteModelResult = async (essayId: string, modelId: string) => {
    setError(null);
    try {
      await dataService.deleteModelResult(essayId, modelId);
      
      // Update the active essay data to remove the deleted model result
      setActiveEssayData((prev: Partial<Essay>) => {
        const updatedModelResults = { ...prev.modelResults };
        delete updatedModelResults[modelId];
        
        // If this was the currently displayed model in legacy fields, clear them
        const wasCurrentModel = prev.modelUsed === MODELS[modelId]?.name;
        
        return {
          ...prev,
          modelResults: updatedModelResults,
          // Clear legacy fields if this was the current model
          ...(wasCurrentModel && {
            rawMarkingRuns: [],
            consolidatedMarkingResults: [],
            finalOverallScore: null,
            finalOverallBand: null,
            finalConfidence: null,
            ensembleSpread: null,
            feedback: null,
            markingPromptUsed: null,
            justificationGenerationPromptUsed: null,
            metaAnalysisPromptUsed: null,
            modelUsed: null,
            scoringMethodUsed: null,
            scoringCalculationTrace: null,
          })
        };
      });
      
      // Also update the essays list if we're viewing an assessment
      if (viewingAssessment && currentUser) {
        const updatedEssays = await dataService.getEssaysForAssessment(currentUser.uid, viewingAssessment.id);
        setEssaysForViewingAssessment(updatedEssays || []);
      }
      
    } catch (err) {
      handleGlobalError(err);
    }
  };

  const handleSelectAssessment = async (assessment: Assessment) => {
      setIsLoading(true);
      setViewingAssessment(assessment);
      try {
        if (currentUser) {
          const essays = await dataService.getEssaysForAssessment(currentUser.uid, assessment.id);
          setEssaysForViewingAssessment(essays || []);
        }
      } catch (err) {
        handleGlobalError(err);
      } finally {
        setIsLoading(false);
      }
  }

  const renderContent = () => {
    if (isLoading && !currentUser) {
      return <LoadingSpinner message="Initializing..." />;
    }

    if (!currentUser) {
      switch (currentStep) {
        case AppStep.AUTH:
          return <LoginScreen onAuthError={handleAuthError} />;
        case AppStep.ABOUT:
          return <AboutPage onBack={() => setCurrentStep(AppStep.HOME)} />;
        case AppStep.HELP_CONTACT:
          return <HelpContactPage currentUser={null} onBack={() => setCurrentStep(AppStep.HOME)} />;
        case AppStep.CONTACT_US:
          return <ContactUsPage onNavigate={handleContactNavigate} />;
        case AppStep.PRIVACY_POLICY:
          return <PrivacyPolicyPage onBack={() => setCurrentStep(AppStep.HOME)} />;
        case AppStep.REFUND_POLICY:
          return <RefundPolicyPage onBack={() => setCurrentStep(AppStep.HOME)} />;
        case AppStep.TERMS_OF_SERVICE:
          return <TermsOfServicePage onBack={() => setCurrentStep(AppStep.HOME)} />;
        default:
          return (
            <HomePage
              onGetStarted={() => setCurrentStep(AppStep.AUTH)}
              onContact={() => setCurrentStep(AppStep.CONTACT_US)}
              isLoggedIn={false}
            />
          );
      }
    }

    const allowManualScoring = isAdmin || manualScoringEnabled;
    const primaryUploadOptions: Array<{ id: 'single' | 'batch'; title: string; description: string; accent: 'blue' | 'orange' }> = [
      {
        id: 'single',
        title: 'Upload one essay',
        description: 'Great for a quick result on a single student. Upload images, a document, or paste the response.',
        accent: 'blue',
      },
      {
        id: 'batch',
        title: 'Upload multiple essays at once',
        description: 'Perfect for whole-class submissions. Stage each file with a student name, then mark them together.',
        accent: 'orange',
      },
    ];

    const singleMethodDetails: Record<'image' | 'document' | 'text', { title: string; description: string; helper?: string }> = {
      image: {
        title: 'Handwritten Photos/Scans',
        description: 'Upload photos or PDF scans of a handwritten essay.',
        helper: '',
      },
      document: {
        title: 'Document',
        description: 'Upload a DOCX or PDF file.',
        helper: '',
      },
      text: {
        title: 'Paste Text Directly',
        description: 'Paste the essay text directly.',
        helper: '',
      },
    };

    const batchOptionDetails: Record<'pdfs' | 'documents', { title: string; description: string; helper?: string }> = {
      pdfs: {
        title: 'PDFs',
        description: 'Upload multiple PDFs at once.',
        helper: '',
      },
      documents: {
        title: 'Documents',
        description: 'Upload multiple DOCX files at once.',
        helper: '',
      },
    };

    const renderPrimarySelection = () => (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-midnight">
            How would you like to upload essays?
          </h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {primaryUploadOptions.map((option) => (
            <button
              key={option.id}
            onClick={() => {
                setSelectedUploadPath(option.id);
                setSingleUploadMethod(null);
                setSelectedBatchOption(null);
              }}
              className={`p-6 rounded-2xl border shadow-soft hover:shadow-lg transition-all flex flex-col sm:flex-row sm:items-center gap-4 text-center sm:text-left ${
                option.accent === 'blue'
                  ? 'border-blue-200 hover:border-blue-400 focus:outline-blue-500'
                  : 'border-orange-200 hover:border-orange-400 focus:outline-orange-500'
              } bg-white`}
            >
              <img
                src={option.accent === 'blue'
                  ? 'https://firebasestorage.googleapis.com/v0/b/history-essay-bot.firebasestorage.app/o/PWA%20Logos%2FSingle%20Document.png?alt=media&token=2bc68fc3-b973-402b-95c0-5e61170eb7d6'
                  : 'https://firebasestorage.googleapis.com/v0/b/history-essay-bot.firebasestorage.app/o/PWA%20Logos%2FMultiple%20Documents.png?alt=media&token=604cbf4b-9a30-41e2-a9d3-099ac1380ef6'}
                alt={option.accent === 'blue' ? 'Single upload' : 'Batch upload'}
                className="h-16 w-16 sm:h-24 sm:w-24 object-contain drop-shadow-sm mx-auto sm:mx-0"
              />
              <div className="flex flex-col items-center sm:items-start gap-1 w-full px-2 sm:px-6">
                <p className={`uppercase font-extrabold tracking-wide ${option.accent === 'blue' ? 'text-blue-600' : 'text-orange-600'} text-2xl`}>
                  {option.accent === 'blue' ? 'Single upload' : 'Batch upload'}
                </p>
                <h3 className="text-xl font-bold text-midnight mt-1">{option.title}</h3>
              </div>
            </button>
          ))}
        </div>
      </div>
    );

    const renderSingleOptionSelection = () => (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-midnight">
            Choose the format for this essay
          </h2>
        </div>
        <div className="flex flex-col md:flex-row md:items-stretch md:justify-center gap-4 md:gap-6">
          {(Object.keys(singleMethodDetails) as Array<'image' | 'document' | 'text'>).map((methodKey, idx, arr) => {
            const detail = singleMethodDetails[methodKey];
            const iconSrc = methodKey === 'image'
              ? 'https://firebasestorage.googleapis.com/v0/b/history-essay-bot.firebasestorage.app/o/scan.png?alt=media&token=31097071-a098-471d-ac2a-75afd0f94650'
              : methodKey === 'document'
              ? 'https://firebasestorage.googleapis.com/v0/b/history-essay-bot.firebasestorage.app/o/document.png?alt=media&token=cf848fcc-f0e0-4b1c-8174-c70e5480ece6'
              : 'https://firebasestorage.googleapis.com/v0/b/history-essay-bot.firebasestorage.app/o/paste.png?alt=media&token=75c4d989-84ed-4682-9a8f-ca6a2dd6e323';
            return (
              <React.Fragment key={methodKey}>
                <button
                  onClick={() => setSingleUploadMethod(methodKey)}
                  className="flex-1 text-left p-5 rounded-2xl border border-blue-100 bg-white hover:border-blue-400 hover:shadow-md transition-all min-w-[220px]"
                >
                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 justify-center sm:justify-start">
                    <img src={iconSrc} alt={detail.title} className="h-16 w-16 sm:h-24 sm:w-24 object-contain" />
                    <div className="text-center sm:text-left">
                      <h3 className="text-2xl font-extrabold text-blue-600 mb-2">{detail.title}</h3>
                      <p className="text-base font-semibold text-midnight mb-1">{detail.description}</p>
                      {detail.helper && <p className="text-xs text-gray-500 mt-2">{detail.helper}</p>}
                    </div>
                  </div>
                </button>
                {idx < arr.length - 1 && (
                  <div className="flex items-center justify-center">
                    <span className="px-2 text-lg font-bold text-gray-400">OR</span>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );

    const renderBatchOptionSelection = () => (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-midnight">Choose the file type for your batch</h2>
        </div>
        <div className="flex flex-col md:flex-row md:items-stretch md:justify-center gap-4 md:gap-6">
          {(Object.keys(batchOptionDetails) as Array<'pdfs' | 'documents'>).map((optionKey, idx, arr) => {
            const detail = batchOptionDetails[optionKey];
            const iconSrc = optionKey === 'pdfs'
              ? 'https://firebasestorage.googleapis.com/v0/b/history-essay-bot.firebasestorage.app/o/PDF.png?alt=media&token=09c846fb-73e9-4c22-926b-6c65b20887ba'
              : 'https://firebasestorage.googleapis.com/v0/b/history-essay-bot.firebasestorage.app/o/document.png?alt=media&token=cf848fcc-f0e0-4b1c-8174-c70e5480ece6';
            return (
              <React.Fragment key={optionKey}>
                <button
                  onClick={() => setSelectedBatchOption(optionKey)}
                  className="flex-1 text-left p-6 rounded-2xl border border-orange-200 bg-white hover:border-orange-400 hover:shadow-md transition-all min-w-[240px]"
                >
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 justify-center sm:justify-start">
                <img src={iconSrc} alt={detail.title} className="h-16 w-16 sm:h-20 sm:w-20 object-contain" />
                <div className="text-center md:text-left">
                  <h3 className="text-2xl font-extrabold text-orange-600 mb-2">
                    {detail.title}
                  </h3>
                  <p className="text-base font-semibold text-midnight mb-1">{detail.description}</p>
                  {detail.helper && <p className="text-xs text-gray-500 mt-2">{detail.helper}</p>}
                </div>
                  </div>
                </button>
                {idx < arr.length - 1 && (
                  <div className="flex items-center justify-center">
                    <span className="px-2 text-lg font-bold text-gray-400">OR</span>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );

    const renderSingleForm = () => {
      if (!singleUploadMethod) return null;
      const detail = singleMethodDetails[singleUploadMethod];
      return (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              className="text-sm text-blue-700 hover:text-blue-900"
              onClick={() => resetUploadFlow()}
            >
              Start over with a different upload type
            </button>
          </div>
          <EssayDetailsForm
            assessmentName={viewingAssessment?.name}
            initialStudentName={activeEssayData?.studentName}
            initialImages={activeEssayData?.uploadedImages}
            onSubmit={processAndMarkEssay}
            onBack={() => {
                setCurrentStep(AppStep.CHOOSE_ASSESSMENT);
            }}
            isProcessing={isLoading || currentStep === AppStep.PROCESSING}
            isAdmin={isAdmin}
            initialInputMethod={singleUploadMethod}
            lockInputMethod
          />
        </div>
      );
    };

    const renderBatchPanel = () => {
      if (!selectedBatchOption) return null;
      const detail = batchOptionDetails[selectedBatchOption];
      return (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              className="text-sm text-blue-700 hover:text-blue-900"
              onClick={() => resetUploadFlow()}
            >
              Start over with a different upload type
            </button>
          </div>
          <BatchUploadPanel
            assessmentName={viewingAssessment?.name}
            onStartBatch={() => {
              // Placeholder for future batch processing hook.
            }}
            isProcessing={isLoading || currentStep === AppStep.PROCESSING}
          />
        </div>
      );
    };

    const renderUploadFlow = () => {
      if (!selectedUploadPath) {
        return renderPrimarySelection();
      }
      if (selectedUploadPath === 'single') {
        if (!singleUploadMethod) {
          return renderSingleOptionSelection();
        }
        return renderSingleForm();
      }
      if (selectedUploadPath === 'batch') {
        if (!selectedBatchOption) {
          return renderBatchOptionSelection();
        }
        return renderBatchPanel();
      }
      return null;
    };

    switch (currentStep) {
      case AppStep.AUTH:
        return <LoadingSpinner message="Signing you in..." />;
      case AppStep.HOME:
        return (
          <HomePage
            onGetStarted={() => setCurrentStep(AppStep.CHOOSE_ASSESSMENT)}
            onContact={() => setCurrentStep(AppStep.CONTACT_US)}
            isLoggedIn={true}
          />
        );
      case AppStep.CHOOSE_ASSESSMENT:
        return (
          <AssessmentSelection
            assessments={assessments}
            isLoading={isLoading}
            isAdmin={isAdmin}
            manualScoringEnabled={allowManualScoring}
            onSelectAssessment={handleSelectAssessment}
            onCreateNew={() => {
              setSelectedAssessmentForEdit(null);
              setCurrentStep(AppStep.CREATE_ASSESSMENT);
            }}
            onEditAssessment={(assessmentId) => {
                const assessmentToEdit = assessments.find(a => a.id === assessmentId) || null;
                setSelectedAssessmentForEdit(assessmentToEdit);
                setCurrentStep(AppStep.CREATE_ASSESSMENT);
            }}
            onDeleteAssessment={handleDeleteAssessment}
            onArchiveAssessment={handleArchiveAssessment}
            onUnarchiveAssessment={handleUnarchiveAssessment}
            showingArchived={showingArchived}
            onToggleArchiveView={handleToggleArchiveView}
            viewingAssessment={viewingAssessment}
            essaysForViewingAssessment={essaysForViewingAssessment}
            onMarkNewEssay={(assessment) => {
                setActiveEssayData(initialEssayDataState);
                setViewingAssessment(assessment);
                resetUploadFlow();
                setActiveProcessingContext(null);
                setCurrentStep(AppStep.UPLOAD_ESSAY_DETAILS);
            }}
            onViewEssayResults={(essay) => {
                setActiveEssayData(essay);
                setActiveProcessingContext(null);
                setCurrentStep(AppStep.SHOW_RESULTS);
            }}
            onRemarkEssay={handleRemark}
            onRequireCreditConfirmation={handleCreditConfirmation}
            onDeleteEssay={handleDeleteEssay}
            onBatchDeleteEssays={handleBatchDeleteEssays}
            onDeleteModelResult={handleDeleteModelResult}
            sortOrder={essaySortOrder}
            onSortOrderChange={setEssaySortOrder}
            setCurrentStep={setCurrentStep}
            onBackToAssessments={goToAllAssessments}
          />
        );
      case AppStep.CREATE_ASSESSMENT:
        return (
          <CreateAssessmentForm
            onSave={handleSaveAssessment}
            onCancel={() => {
                setSelectedAssessmentForEdit(null);
                setCurrentStep(AppStep.CHOOSE_ASSESSMENT);
            }}
            existingAssessment={selectedAssessmentForEdit}
          />
        );
      case AppStep.UPLOAD_ESSAY_DETAILS:
        return (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            <div className="bg-white border border-secondary-100 rounded-2xl shadow-soft px-4 sm:px-6 py-4 max-w-5xl mx-auto">
              <StepIndicator
                steps={uploadProgressConfig.steps}
                currentStep={uploadProgressConfig.current}
              />
            </div>
            {renderUploadFlow()}
          </div>
        );
      case AppStep.PROCESSING:
        return (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            <div className="bg-white border border-secondary-100 rounded-2xl shadow-soft px-4 sm:px-6 py-4 max-w-5xl mx-auto">
              <StepIndicator
                steps={activeProcessingContext === 'remark' ? remarkProgressConfig.steps : uploadProgressConfig.steps}
                currentStep={activeProcessingContext === 'remark' ? remarkProgressConfig.current : uploadProgressConfig.current}
              />
            </div>
            <LoadingSpinner message={processingMessage} />
          </div>
        );
      case AppStep.SHOW_RESULTS:
        return (
          <div className="space-y-6">
            {(activeProcessingContext === 'remark' || activeProcessingContext === 'marking') && (
              <div className="bg-white border border-secondary-100 rounded-2xl shadow-soft px-4 sm:px-6 py-4 max-w-5xl mx-auto">
                <StepIndicator
                  steps={activeProcessingContext === 'remark' ? remarkProgressConfig.steps : uploadProgressConfig.steps}
                  currentStep="results"
                />
              </div>
            )}
            <ResultsDisplay
              essayData={activeEssayData}
              assessment={assessments.find(a => a.id === activeEssayData.assessmentId)}
              onStartOver={handleStartOver}
              onRemark={() => handleRemark(activeEssayData as Essay)}
              isProcessing={isLoading || currentStep === AppStep.PROCESSING}
              onBackToEssays={() => {
                  if (viewingAssessment) {
                      handleSelectAssessment(viewingAssessment);
                  }
                  setCurrentStep(AppStep.CHOOSE_ASSESSMENT);
              }}
              onSaveManualMarks={handleSaveManualMarks}
              onExplainVariance={handleExplainVariance}
              onDeleteModelResult={handleDeleteModelResult}
              isAdmin={isAdmin}
              manualScoringEnabled={allowManualScoring}
            />
          </div>
        );
      case AppStep.ADMIN_DASHBOARD:
        return <AdminDashboard onBack={() => setCurrentStep(AppStep.CHOOSE_ASSESSMENT)} />;
      case AppStep.ACCOUNT:
        return <AccountPage currentUser={currentUser} onBack={() => setCurrentStep(AppStep.CHOOSE_ASSESSMENT)} />;
      case AppStep.ABOUT:
        return <AboutPage onBack={() => setCurrentStep(AppStep.CHOOSE_ASSESSMENT)} />;
      case AppStep.HELP_CONTACT:
        return <HelpContactPage currentUser={currentUser} onBack={() => setCurrentStep(AppStep.CHOOSE_ASSESSMENT)} />;
      case AppStep.CONTACT_US:
        return <ContactUsPage onNavigate={handleContactNavigate} />;
      case AppStep.PRIVACY_POLICY:
        return <PrivacyPolicyPage onBack={() => setCurrentStep(AppStep.CHOOSE_ASSESSMENT)} />;
      case AppStep.REFUND_POLICY:
        return <RefundPolicyPage onBack={() => setCurrentStep(AppStep.CHOOSE_ASSESSMENT)} />;
      case AppStep.TERMS_OF_SERVICE:
        return <TermsOfServicePage onBack={() => setCurrentStep(AppStep.CHOOSE_ASSESSMENT)} />;
      default:
        return <p>Unknown application state.</p>;
    }
  };

  // Compute breadcrumbs for DashboardLayout
  const breadcrumbs = useMemo(() => {
    const crumbs: Array<{ label: string; onClick?: () => void }> = [];
    
    // Always start with Dashboard for authenticated steps
    if (currentUser && currentStep !== AppStep.CHOOSE_ASSESSMENT) {
      crumbs.push({ label: 'Dashboard', onClick: goToAllAssessments });
    }
    
    // Add assessment name if viewing one
    if (viewingAssessment) {
      crumbs.push({ 
        label: viewingAssessment.name, 
        onClick: currentStep !== AppStep.CHOOSE_ASSESSMENT ? goToAssessmentDetails : undefined 
      });
    }
    
    // Add current page
    const stepLabels: Partial<Record<AppStep, string>> = {
      [AppStep.CHOOSE_ASSESSMENT]: viewingAssessment ? 'Essays' : 'Assessments',
      [AppStep.CREATE_ASSESSMENT]: selectedAssessmentForEdit ? 'Edit Assessment' : 'New Assessment',
      [AppStep.UPLOAD_ESSAY_DETAILS]: 'Upload Essay',
      [AppStep.PROCESSING]: 'Processing',
      [AppStep.SHOW_RESULTS]: activeEssayData?.studentName || 'Results',
      [AppStep.ADMIN_DASHBOARD]: 'Admin Dashboard',
      [AppStep.ACCOUNT]: 'My Account',
      [AppStep.ABOUT]: 'About',
      [AppStep.HELP_CONTACT]: 'Help & Support',
      [AppStep.CONTACT_US]: 'Contact Us',
      [AppStep.PRIVACY_POLICY]: 'Privacy Policy',
      [AppStep.REFUND_POLICY]: 'Refund Policy',
      [AppStep.TERMS_OF_SERVICE]: 'Terms of Service',
    };
    
    const currentLabel = stepLabels[currentStep];
    if (currentLabel && currentStep !== AppStep.HOME) {
      crumbs.push({ label: currentLabel });
    }
    
    return crumbs;
  }, [currentStep, currentUser, viewingAssessment, selectedAssessmentForEdit, activeEssayData?.studentName, goToAllAssessments, goToAssessmentDetails]);

  const backButtonConfig = (() => {
    if (!currentUser) return null;

    const defaultBack = viewingAssessment ? {
      label: 'Back to assessment',
      action: goToAssessmentDetails,
    } : {
      label: 'Back to assessments',
      action: goToAllAssessments,
    };

    switch (currentStep) {
      case AppStep.CHOOSE_ASSESSMENT:
        if (viewingAssessment) {
          return {
            label: 'Back to all assessments',
            action: () => {
              resetUploadFlow();
              setViewingAssessment(null);
            },
          };
        }
        return null;
      case AppStep.CREATE_ASSESSMENT:
        return {
          label: 'Back to assessments',
          action: () => {
            setSelectedAssessmentForEdit(null);
            setCurrentStep(AppStep.CHOOSE_ASSESSMENT);
          },
        };
      case AppStep.UPLOAD_ESSAY_DETAILS:
        if (!selectedUploadPath) {
          return viewingAssessment ? {
            label: 'Back to assessment',
            action: goToAssessmentDetails,
          } : {
            label: 'Back to assessments',
            action: goToAllAssessments,
          };
        }
        if (selectedUploadPath === 'single') {
          if (!singleUploadMethod) {
            return {
              label: 'Back to upload type selection',
              action: () => setSelectedUploadPath(null),
            };
          }
          return {
            label: 'Back to single upload options',
            action: () => setSingleUploadMethod(null),
          };
        }
        if (selectedUploadPath === 'batch') {
          if (!selectedBatchOption) {
            return {
              label: 'Back to upload type selection',
              action: () => setSelectedUploadPath(null),
            };
          }
          return {
            label: 'Back to batch options',
            action: () => setSelectedBatchOption(null),
          };
        }
        return defaultBack;
      case AppStep.PROCESSING:
        return defaultBack;
      case AppStep.SHOW_RESULTS:
        return {
          label: viewingAssessment ? `Back to ${viewingAssessment.name}` : 'Back to assessments',
          action: viewingAssessment ? goToAssessmentDetails : goToAllAssessments,
        };
      case AppStep.ADMIN_DASHBOARD:
      case AppStep.ACCOUNT:
      case AppStep.ABOUT:
      case AppStep.HELP_CONTACT:
      case AppStep.CONTACT_US:
      case AppStep.PRIVACY_POLICY:
      case AppStep.REFUND_POLICY:
      case AppStep.TERMS_OF_SERVICE:
        return {
          label: 'Back to home',
          action: () => setCurrentStep(AppStep.CHOOSE_ASSESSMENT),
        };
      default:
        return null;
    }
  })();

  // Render modals (shared between both layouts)
  const renderModals = () => (
    <>
      <ApiKeyHelpModal isOpen={showApiKeyHelp} onClose={() => window.location.reload()} />
      <DomainAuthorizationHelpModal isOpen={showDomainAuthHelp} onClose={() => setShowDomainAuthHelp(false)} />
      <QuotaHelpModal isOpen={showQuotaHelp} onClose={() => setShowQuotaHelp(false)} />
      <StorageRulesHelpModal isOpen={showStorageRulesHelp} onClose={() => setShowStorageRulesHelp(false)} />
      <ConfirmModal 
        isOpen={confirmModalState.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmModalState.onConfirm}
        title={confirmModalState.title}
        message={confirmModalState.message}
        confirmButtonText={confirmModalState.confirmButtonText}
        loadingText={confirmModalState.loadingText}
        isLoading={isLoading}
      />
      
      <MarkingModelsSelectionModal
        isOpen={showMarkingModelsModal}
        onClose={handleModelSelectionCancel}
        onConfirm={handleModelSelectionConfirm}
        userEmail={currentUser?.email || undefined}
      />
      
      {currentUser && (
        <CreditConfirmationModal
          isOpen={showCreditConfirmationModal}
          onClose={handleCreditConfirmationClose}
          onConfirm={handleCreditConfirmationConfirm}
          onPurchaseCredits={handlePurchaseCredits}
          creditsRequired={creditConfirmationData?.creditsRequired || 0}
          actionDescription={creditConfirmationData?.actionDescription || ''}
          currentUser={currentUser}
        />
      )}
      
      <CreditPurchaseModal
        isOpen={showCreditPurchaseModal}
        onClose={handleCreditPurchaseModalClose}
        onPurchaseCredits={handlePurchaseCredits}
      />
      
      {error && (
        <div role="alert" className="fixed bottom-5 right-5 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-xl max-w-md z-50">
          <div className="flex">
              <div className="py-1"><svg className="fill-current h-6 w-6 text-red-500 mr-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 9a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1zm1 2a1 1 0 100 2 1 1 0 000-2z"/></svg></div>
              <div>
                  <p className="font-bold">An Error Occurred</p>
                  <p className="text-sm">{error}</p>
              </div>
               <button onClick={() => setError(null)} className="absolute top-0 bottom-0 right-0 px-4 py-3" aria-label="Close">
                  <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><title>Close</title><path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/></svg>
              </button>
          </div>
        </div>
      )}
    </>
  );

  // Authenticated users get the new DashboardLayout with sidebar
  if (currentUser) {
    return (
      <>
        <DashboardLayout
          currentStep={currentStep}
          onNavigate={setCurrentStep}
          isAdmin={isAdmin}
          creditBalance={creditBalance}
          userEmail={currentUser.email || undefined}
          onSignOut={() => signOut(auth)}
          onBuyCredits={handleShowCreditPurchaseModal}
          breadcrumbs={breadcrumbs}
          backButton={backButtonConfig}
        >
          <ContentContainer>
            {renderContent()}
          </ContentContainer>
        </DashboardLayout>
        {renderModals()}
      </>
    );
  }

  // Unauthenticated users get the original layout with header/footer
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="w-full">
        <header className="bg-card shadow-soft safe-area-top border-b border-border">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
            <div className="flex justify-between items-center py-3 sm:py-4 gap-2">
              <button
                type="button"
                onClick={() => setCurrentStep(AppStep.HOME)}
                className="flex items-center min-w-0 flex-1 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary rounded-xl px-1 py-1 -ml-1"
              >
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/history-essay-bot.firebasestorage.app/o/transparent%20logo%20market%20mate%20(2).webp?alt=media&token=577c40fb-e028-4260-b65d-3545512569dd"
                  alt="AI Marker Mate Logo"
                  className="h-8 sm:h-10 w-auto mr-2 flex-shrink-0"
                />
                <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-foreground truncate">{APP_TITLE}</h1>
              </button>
              <div className="flex items-center gap-1 sm:gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className="hidden sm:inline-flex"
                  onClick={() => setCurrentStep(AppStep.CONTACT_US)}
                >
                  Contact
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentStep(AppStep.AUTH)}
                >
                  Log in
                </Button>
                <Button
                  size="sm"
                  onClick={() => setCurrentStep(AppStep.AUTH)}
                >
                  Try it now
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 py-4 sm:py-8 px-3 sm:px-4 lg:px-8">
          <div className="max-w-7xl mx-auto space-y-4">
            {renderContent()}
          </div>
        </main>

        <Footer onNavigate={setCurrentStep} />

        <ApiKeyHelpModal isOpen={showApiKeyHelp} onClose={() => window.location.reload()} />
        <DomainAuthorizationHelpModal isOpen={showDomainAuthHelp} onClose={() => setShowDomainAuthHelp(false)} />
        <QuotaHelpModal isOpen={showQuotaHelp} onClose={() => setShowQuotaHelp(false)} />
        <StorageRulesHelpModal isOpen={showStorageRulesHelp} onClose={() => setShowStorageRulesHelp(false)} />
        <ConfirmModal 
          isOpen={confirmModalState.isOpen}
          onClose={closeConfirmModal}
          onConfirm={confirmModalState.onConfirm}
          title={confirmModalState.title}
          message={confirmModalState.message}
          confirmButtonText={confirmModalState.confirmButtonText}
          loadingText={confirmModalState.loadingText}
          isLoading={isLoading}
        />
        
        <MarkingModelsSelectionModal
          isOpen={showMarkingModelsModal}
          onClose={handleModelSelectionCancel}
          onConfirm={handleModelSelectionConfirm}
          userEmail={currentUser?.email || undefined}
        />
        
        {currentUser && (
          <CreditConfirmationModal
            isOpen={showCreditConfirmationModal}
            onClose={handleCreditConfirmationClose}
            onConfirm={handleCreditConfirmationConfirm}
            onPurchaseCredits={handlePurchaseCredits}
            creditsRequired={creditConfirmationData?.creditsRequired || 0}
            actionDescription={creditConfirmationData?.actionDescription || ''}
            currentUser={currentUser}
          />
        )}
        
        <CreditPurchaseModal
          isOpen={showCreditPurchaseModal}
          onClose={handleCreditPurchaseModalClose}
          onPurchaseCredits={handlePurchaseCredits}
        />
        
        {error && (
          <div role="alert" className="fixed bottom-5 right-5 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-xl max-w-md z-50">
            <div className="flex">
                <div className="py-1"><svg className="fill-current h-6 w-6 text-red-500 mr-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 9a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1zm1 2a1 1 0 100 2 1 1 0 000-2z"/></svg></div>
                <div>
                    <p className="font-bold">An Error Occurred</p>
                    <p className="text-sm">{error}</p>
                </div>
                 <button onClick={() => setError(null)} className="absolute top-0 bottom-0 right-0 px-4 py-3" aria-label="Close">
                    <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><title>Close</title><path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/></svg>
                </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
